import React from "react";

// ─── Enums & Constants ─────────────────────────────────────────────────────────

export const NUMERIC_STATUS = {
  0: "Pending", 1: "InProgress", 2: "Completed", 3: "Failed", 4: "Deleted",
};

export const STATUS_META = {
  pending:    { label: "Pending",     bg: "#dbeafe", color: "#1d4ed8", dot: "#3b82f6" },
  inprogress: { label: "In Progress", bg: "#fef3c7", color: "#b45309", dot: "#f59e0b" },
  completed:  { label: "Completed",   bg: "#dcfce7", color: "#15803d", dot: "#22c55e" },
  failed:     { label: "Failed",      bg: "#fee2e2", color: "#b91c1c", dot: "#ef4444" },
  deleted:    { label: "Deleted",     bg: "#f3f4f6", color: "#6b7280", dot: "#9ca3af" },
};

export const STATUS_FILTER_OPTIONS = [
  { value: "ALL",        label: "All",         color: "#4b5563", bg: "#f3f4f6" },
  { value: "Pending",    label: "Pending",     ...STATUS_META.pending },
  { value: "InProgress", label: "In Progress", ...STATUS_META.inprogress },
  { value: "Completed",  label: "Completed",   ...STATUS_META.completed },
  { value: "Failed",     label: "Failed",      ...STATUS_META.failed },
];

// ─── Data Helpers ──────────────────────────────────────────────────────────────

export function getStatus(batch) {
  const raw = batch.status ?? batch.Status;
  if (typeof raw === "number") return NUMERIC_STATUS[raw] ?? "Pending";
  if (raw == null) return "Pending";
  const s = String(raw).trim();
  if (s === "" || s === "-") return "Pending";
  if (/^\d+$/.test(s)) return NUMERIC_STATUS[Number(s)] ?? "Pending";
  return s;
}

export const normaliseStatus = (b) =>
  String(getStatus(b)).toLowerCase().replace(/[\s_-]/g, "");

export const getDatabaseName = (batch) =>
  batch.databaseName || batch.DatabaseName || batch.dbName || batch.DbName || "";

export const getArchiveFileName = (batch) => {
  const p = batch.pathToArchiveFile || batch.PathToArchiveFile || "";
  if (!p) return "";
  // Works for both / and \ separators
  return p.split(/[\\/]/).pop() || p;
};

export const getFileType = (batch) =>
  batch.fileType || batch.FileType || "";

export const getRegisteringUser = (batch) =>
  batch.registeringUser || batch.RegisteringUser || "";

export function getCreatedDate(batch) {
  const raw = batch.createdDate || batch.CreatedDate;
  if (!raw) return { date: "", time: "" };
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return { date: String(raw), time: "" };
    return {
      date: d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }),
      time: d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
    };
  } catch {
    return { date: String(raw), time: "" };
  }
}

export function timeAgo(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const ms    = Date.now() - d.getTime();
  const mins  = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  const days  = Math.floor(ms / 86400000);
  if (mins  <  1) return "just now";
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days  <  7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

// ─── Icons ─────────────────────────────────────────────────────────────────────

export function RefreshIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.5 7.5C1.5 4.19 4.19 1.5 7.5 1.5a6 6 0 0 1 4.95 2.62M13.5 7.5C13.5 10.81 10.81 13.5 7.5 13.5a6 6 0 0 1-4.95-2.62"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M12 1.5l.45 2.62L10 4.5M3 13.5l-.45-2.62L5 10.5"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export function TrashIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}

export function RestoreIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
      <path d="M3 3v5h5"/>
    </svg>
  );
}

export function CheckIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

export function XIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

// ─── UI Components ─────────────────────────────────────────────────────────────

function statusMeta(status) {
  const key = String(status || "").toLowerCase().replace(/\s/g, "");
  return STATUS_META[key] || { label: status || "—", bg: "#f3f4f6", color: "#4b5563", dot: "#9ca3af" };
}

export function StatusBadge({ status }) {
  const { label, bg, color, dot } = statusMeta(status);
  if (!status) return <span style={{ color: "#aaa" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, background: bg, color, fontSize: 12, fontWeight: 600 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, flexShrink: 0 }} />
      {label}
    </span>
  );
}

export function PipelineSteps({ status, isProcessed, isDeleted }) {
  const s = String(status || "").toLowerCase().replace(/[\s_-]/g, "");

  const steps =
    isDeleted        ? [{ label: "Uploaded", state: "done" }, { label: "Processing", state: "idle"   }, { label: "Deleted",    state: "failed" }]
    : s === "completed"  ? [{ label: "Uploaded", state: "done" }, { label: "Processing", state: "done"   }, { label: "Indexed",    state: "done"   }]
    : s === "failed"     ? [{ label: "Uploaded", state: "done" }, { label: "Processing", state: "failed" }, { label: "Indexed",    state: "idle"   }]
    : s === "inprogress" ? [{ label: "Uploaded", state: "done" }, { label: "Processing", state: "active" }, { label: "Indexed",    state: "idle"   }]
    :                      [{ label: "Uploaded", state: "done" }, { label: "Processing", state: "idle"   }, { label: "Indexed",    state: "idle"   }];

  const stateStyle = {
    done:   { bg: "#22c55e", border: "#22c55e", text: "#15803d" },
    active: { bg: "#f59e0b", border: "#f59e0b", text: "#b45309" },
    failed: { bg: "#ef4444", border: "#ef4444", text: "#b91c1c" },
    idle:   { bg: "#fff",    border: "#d1d5db", text: "#9ca3af" },
  };
  const iconMap = { done: "✓", active: "●", failed: "✕", idle: "○" };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, minWidth: 190 }}>
      {steps.map((step, i) => {
        const st = stateStyle[step.state];
        return (
          <React.Fragment key={step.label}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <div style={{
                width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                background: step.state === "idle" ? "#fff" : st.bg,
                border: `2px solid ${st.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 800,
                color: step.state === "idle" ? "#d1d5db" : "#fff",
                boxShadow: step.state === "active" ? `0 0 0 3px ${st.bg}33` : "none",
              }}>
                {iconMap[step.state]}
              </div>
              <span style={{ fontSize: 9, color: st.text, fontWeight: 600, whiteSpace: "nowrap", letterSpacing: "0.02em" }}>
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ height: 2, flex: 1, minWidth: 12, background: steps[i].state === "done" ? "#22c55e" : "#e5e7eb", marginBottom: 14, marginLeft: 1, marginRight: 1 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function FileTypeBadge({ type }) {
  if (!type) return null;
  const colors = {
    AFP: { bg: "#ede9fe", color: "#6d28d9" },
    PDF: { bg: "#fce7f3", color: "#be185d" },
  };
  const c = colors[type.toUpperCase()] || { bg: "#f3f4f6", color: "#4b5563" };
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, background: c.bg, color: c.color, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
      {type}
    </span>
  );
}

export function StatCard({ label, value, color, bg, onClick, active }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: "1 1 120px", minWidth: 100, borderRadius: 10, padding: "12px 16px",
      border: active ? `2px solid ${color}` : "2px solid transparent",
      background: active ? bg : "#fff", cursor: "pointer", textAlign: "left", outline: "none",
      boxShadow: active ? `0 0 0 3px ${color}22` : "0 1px 4px rgba(0,0,0,0.06)",
      transition: "all 0.15s",
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: active ? color : "#111", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.04em", color: active ? color : "#6b7280" }}>
        {label}
      </div>
    </button>
  );
}
